import { ICON__circle_half_stroke_solid } from '@/assets/icons_fa';
import { buttonVariants, UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { PLACEHOLDER_BUTTON__bar } from '@/pages/_base/buttons/PLACEHOLDER_BUTTON__bar';
import { HOOK_CANVAS__from_image } from '@/pages/page_generation/layouts/_base/HOOK_CANVAS__from_image';
import { HOOK_IMAGE__get_image_mask_for } from '@/utils/_methods/FN_IMAGE__get_image_mask_for';
import { FN_IMAGE_FILE_DATA__upload_to_workspace_and_set_to_graph } from '@/utils/_methods/FN_IMAGE_FILE_DATA__upload_to_workspace_and_set_to_graph';
import { file_data_from_canvas_offscreen } from '@/utils/UTILS__image';
import { assert_not_null, assert_true } from '@shared/asserts/utils_asserts';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { VariantProps } from 'class-variance-authority';

interface PROPS {
  field: FIELD__image_asset;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  size?: VariantProps<typeof buttonVariants>['size'];
  text?: string;
}

export function G_GRAPH_BUTTON__image_invert_colors({ field, variant = 'no-border-on-bar', text = '', size = 'square-6' }: PROPS) {
  let field_mask = HOOK_IMAGE__get_image_mask_for(field);

  let image = field_mask.asset;

  /*
   * STORE
   */
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);
  const canvas = HOOK_CANVAS__from_image(image);

  /*
   * EVENTS
   */
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();

    const ctx = canvas.getContext('2d');
    assert_not_null(ctx);

    const { width, height } = image.image_size;

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = 255 - data[i];
      data[i + 1] = 255 - data[i + 1];
      data[i + 2] = 255 - data[i + 2];
    }
    ctx.putImageData(imageData, 0, 0);

    const file_data = await file_data_from_canvas_offscreen(canvas);

    await FN_IMAGE_FILE_DATA__upload_to_workspace_and_set_to_graph(file_data, field_mask);
  }

  /*
   * METHODS
   */
  function is_disabled(): boolean {
    return g_general__ui_disabled;
  }

  /*
   * HTML
   */
  if (field_mask.meta.kind__is_output) return <PLACEHOLDER_BUTTON__bar class_name_button="size-6" />;
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) {
    return <PLACEHOLDER_BUTTON__bar class_name_button="size-6" />;
  }

  if (!field_mask.image__is_initialized) return <PLACEHOLDER_BUTTON__bar class_name_button="size-6" />;

  if (field_mask.is_stub()) return <PLACEHOLDER_BUTTON__bar class_name_button="size-6" />;

  assert_true(field_mask.is_category_mask);

  return (
    <UI_PRIMITIVE__button disabled={is_disabled()} {...{ onClick, size, variant }}>
      <ICON__circle_half_stroke_solid className="!size-3" />
      {text}
    </UI_PRIMITIVE__button>
  );
}
